import {
  PROJECTS_PAGINATED_FETCH_STARTED,
  PROJECTS_PAGINATED_FETCH_SUCCEEDED,
  PROJECTS_PAGINATED_FETCH_FAILED,
  PROJECTS_PAGINATED_CHANGE_PAGE,
  PROJECTS_PAGINATED_CLEAR,
  PROJECTS_PAGINATED_SET_PROJECT_DEVICE,
  PROJECTS_PAGINATED_REMOVE_PROJECT_DEVICE
} from '#action/types';
import { webPortalApi } from '#config/web-portal-api';
import { snackBarError } from '#action/fetch';
import { getProjectsIds } from '#component/project/dashboard/helper';
import { getRequestOptions, getURLParameter } from '#helper';

let abortController = new AbortController();

export const fetchPaginatedProjects = () => async (dispatch, getState) => {
  try {
    abortController.abort();
    dispatch({
      type: PROJECTS_PAGINATED_FETCH_STARTED
    });
    const { project } = getState().listFilter;
    const { order, orderBy, page, itemsPerPage } = getState().projectsPaginated;
    const p = getURLParameter('page') || page;
    const offset = (Number(p) - 1) * itemsPerPage;
    const searchParams = new URLSearchParams({
      order,
      orderBy,
      offset,
      limit: 50,
      pagination: 1
    });
    const projectsIds = [];
    const searchValue = project?.search?.value;
    const deviceValue = project?.device?.value;
    const userValue = project?.user?.value;
    const unexpiredValue = project?.unexpired?.value;
    const devicesAndUsers = deviceValue?.length && userValue?.length;
    const subprojectValue = project?.subproject?.value;

    if (unexpiredValue) {
      const [unexpiredData] = project.unexpired.value;
      const unexpired = unexpiredData.data.value;
      if (unexpired === '1') { // Active only
        searchParams.set('unexpired', unexpired);
      } else {
        searchParams.delete('unexpired');
      }
    }

    if (searchValue) {
      searchParams.set('filter', searchValue);
      searchParams.set('filterBy', 'title');
    }

    if (subprojectValue) {
      const [subprojectData] = project.subproject.value;
      const subproject = subprojectData.data.value;

      if (subproject === '1' && searchValue) {
        searchParams.set('subproject', subproject);
        searchParams.set('filterBy', 'title');
      } else {
        searchParams.delete('subproject');
        if (!searchValue) {
          searchParams.delete('filterBy', 'title');
        }
      }
    }

    if (devicesAndUsers) {
      const idsByDevice = getProjectsIds(project.device, 'projects');
      const idsByUser = getProjectsIds(project.user, 'directProjects');
      const commonIds = idsByDevice.filter(id => idsByUser.includes(id));
      projectsIds.push(...commonIds);
    } else if (deviceValue?.length) {
      const ids = getProjectsIds(project.device, 'projects');
      projectsIds.push(...ids);
    } else if (userValue?.length) {
      const ids = getProjectsIds(project.user, 'directProjects');
      projectsIds.push(...ids);
    }

    if (projectsIds.length) {
      const ids = [...new Set(projectsIds)].join(',');
      searchParams.set('ids', ids);
    }

    abortController = new AbortController();
    const { signal } = abortController;

    // Debounce to prevent sending multiple requests in a short time
    await new Promise(res => {
      setTimeout(() => {
        res();
      }, 200);
    });

    if (!projectsIds.length && devicesAndUsers && !searchValue) {
      dispatch({
        type: PROJECTS_PAGINATED_FETCH_SUCCEEDED,
        payload: {
          projects: []
        },
        totalCount: undefined
      });

      return;
    }

    const projectPaginationURL = webPortalApi.buildApiUrl(`project/?${searchParams.toString()}`);
    const res = await fetch(projectPaginationURL, { ...getRequestOptions(), signal });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not JSON');
    }

    if (!res.ok) {
      dispatch(snackBarError({ message: 'Error in request' }));
    }

    // Project resp logic
    const { projects = [], last } = await res.json();
    const ids = projects.map(pr => pr.id).join(',');

    if (!projects.length) {
      dispatch({
        type: PROJECTS_PAGINATED_FETCH_SUCCEEDED,
        payload: {
          projects
        },
        totalCount: last
      });

      return;
    }

    if (ids) {
      const requests = [
        fetch(webPortalApi.buildApiUrl(`device/count?project=${ids}`), getRequestOptions()),
        fetch(webPortalApi.buildApiUrl(`user/count?project=${ids}`), getRequestOptions())
      ];

      const responses = await Promise.all(requests);

      const contentTypes = responses.map(r => r.headers.get('content-type'));
      if (contentTypes.some(ct => (!ct || !ct.includes('application/json')))) {
        throw new TypeError('Response is not json');
      }

      const notOkResponses = responses.filter(r => !r.ok);
      if (notOkResponses.length) {
        throw await Promise.all(notOkResponses.map(r => r.json()));
      }

      const [
        deviceCount,
        userCount
      ] = await Promise.all(responses.map(r => r.json()));

      dispatch({
        type: PROJECTS_PAGINATED_FETCH_SUCCEEDED,
        payload: {
          projects,
          users: userCount,
          devices: deviceCount
        },
        totalCount: last
      });
    }
  } catch (e) {
    if (e.name === 'AbortError') {
      return;
    }
    dispatch(
      snackBarError({
        message: 'Error in request'
      })
    );
    dispatch({
      type: PROJECTS_PAGINATED_FETCH_FAILED,
      error: e
    });
  }
};

export const changePage = (newPage, { refetch = true } = {}) => dispatch => {
  dispatch({
    type: PROJECTS_PAGINATED_CHANGE_PAGE,
    payload: newPage
  });

  if (refetch) {
    dispatch(fetchPaginatedProjects());
  }
};

// Update project devices count.
export const setProjectDevices = (projectId, devicesCount) => dispatch => {
  dispatch({
    type: PROJECTS_PAGINATED_SET_PROJECT_DEVICE,
    payload: { projectId, devices: devicesCount }
  });
};

// Update project devices count.
export const removeProjectDevices = (projectId, devicesCount) => dispatch => {
  dispatch({
    type: PROJECTS_PAGINATED_REMOVE_PROJECT_DEVICE,
    payload: { projectId, devices: devicesCount }
  });
};

export const clearPaginatedProjects = () => dispatch => {
  abortController.abort();
  dispatch({
    type: PROJECTS_PAGINATED_CLEAR
  });
};
